import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import Icon from '@src/basic-components/Icon/Icon';
import type { CommentCollectionType } from '@src/store/comments/comments';
import { useComment } from '@src/store/comments/hooks/useComment';
import { usePaginatedComments } from '@src/store/comments/hooks/usePaginatedComments';
import type { CommentSortOrder } from '@src/store/contentEntities/model';
import { useTranslation } from 'react-i18next';

interface LoadMoreCommentsButtonProps {
    className?: string;
    tokenName: string;
    userId?: string; // required if type is 'user'
    postId?: string; // required if type is 'post'
    parentCommentId?: string; // required if type is 'comment'
    includeDeleted?: boolean;
    type: CommentCollectionType;
    sortOrder?: CommentSortOrder;
}

export function LoadMoreCommentsButton(props: LoadMoreCommentsButtonProps) {
    const { className = '', tokenName, type, includeDeleted, parentCommentId = '', postId = '', userId = '', sortOrder } = props;
    const parentComment = useComment(parentCommentId);

    const { loadMore, loadStatus, commentIds } = usePaginatedComments({
        type,
        fetchLimit: 20,
        parentCommentId,
        postId,
        tokenName,
        userId,
        includeDeleted,
        includeMyReactions: true,
        sortOrder,
    });
    const isLoading = loadStatus === 'loading';
    function getBaseClassName() {
        const base = 'text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-50';
        return cn(base, 'flex items-center space-x-1');
    }

    function getTotalMoreReplies() {
        if (type !== 'comment') return 0;
        if (!parentComment || !parentComment.numComments) return 0;
        return parentComment.numComments - commentIds.length;
    }

    const { t } = useTranslation();

    const totalMoreReplies = getTotalMoreReplies();

    // Don't show the button if there are no more replies to load
    if (type === 'comment' && totalMoreReplies <= 0) {
        return null;
    }

    return (
        <Button color="base" variant="ghost" size="xs" onClick={loadMore} isLoading={isLoading} className={cn(getBaseClassName(), className)}>
            <Icon className={cn('text-sm', isLoading ? 'animate-spin' : '')} name="refresh" />
            <Text>{t(`commentList.${type}.loadMoreButton`, { count: totalMoreReplies })}</Text>
        </Button>
    );
}
